import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Loader2, Check } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const loc = useLocation();
  const nav = useNavigate();
  const token = new URLSearchParams(loc.search).get("token") || "";

  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!token) setErr("This reset link is missing its token. Request a new one.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 6) {
      setErr("Password must be at least 6 characters.");
      return;
    }
    if (pw !== pw2) {
      setErr("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Password updated");
      setTimeout(() => nav("/login"), 1500);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't reset password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-newsprint flex items-center justify-center px-6">
      <div className="max-w-md w-full bg-white border border-gray-300 rounded-2xl p-8 fade-up shadow-[6px_6px_0_0_rgba(124,58,237,0.08)]">
        <div className="font-mono text-xs uppercase tracking-[0.25em] text-gray-500 mb-2">
          Account · New password
        </div>
        <h1 className="font-display text-3xl tracking-[-0.02em] font-medium leading-tight">
          Set a new password.
        </h1>

        {done ? (
          <div data-testid="reset-done" className="mt-6">
            <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-brand-gradient text-white">
              <Check size={18} strokeWidth={2} />
            </div>
            <p className="text-sm text-gray-700 mt-4">
              Your password is updated. Taking you to sign in…
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="reset-form">
            <Field label="New password" type="password" value={pw} onChange={setPw} testid="reset-password" />
            <Field label="Confirm password" type="password" value={pw2} onChange={setPw2} testid="reset-password-confirm" />
            {err && (
              <div data-testid="reset-error" className="text-sm text-red-600 border border-red-200 bg-red-50 px-3 py-2 rounded-lg">
                {err}
              </div>
            )}
            <button
              data-testid="reset-submit"
              disabled={busy || !token}
              className="w-full bg-black text-white hover:bg-gray-800 disabled:opacity-60 rounded-lg py-3 font-medium inline-flex items-center justify-center gap-2"
            >
              {busy && <Loader2 size={16} className="animate-spin" />}
              Update password
            </button>
          </form>
        )}

        <div className="mt-6 pt-6 border-t border-dashed border-gray-200 text-sm text-gray-600">
          <Link to="/login" className="text-[#7C3AED] hover:underline">Back to sign in</Link>
        </div>
      </div>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, testid }) {
  return (
    <div>
      <label className="font-mono text-xs uppercase tracking-widest text-gray-500">{label}</label>
      <input
        data-testid={testid}
        type={type}
        required
        minLength={type === "password" ? 6 : undefined}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full mt-1 border border-gray-300 focus:border-[#7C3AED] focus:ring-1 focus:ring-[#7C3AED] outline-none px-3 py-2.5 text-sm bg-white rounded-lg"
      />
    </div>
  );
}
